import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card'

const SESSION_STATUS = {
  CHECKING: 'checking',
  READY: 'ready',
  INVALID: 'invalid'
}

const UpdatePasswordPage = () => {
  const navigate = useNavigate()
  const [sessionStatus, setSessionStatus] = useState(SESSION_STATUS.CHECKING)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const recoveryWaitTimeoutRef = useRef(null)
  const redirectTimeoutRef = useRef(null)

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const searchParams = new URLSearchParams(window.location.search)
    const hasRecoveryHint =
      hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery'

    const clearRecoveryTimeout = () => {
      if (recoveryWaitTimeoutRef.current) {
        clearTimeout(recoveryWaitTimeoutRef.current)
        recoveryWaitTimeoutRef.current = null
      }
    }

    const clearRedirectTimeout = () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
    }

    const markReady = () => {
      clearRecoveryTimeout()
      setSessionStatus(SESSION_STATUS.READY)
    }

    const markInvalid = () => {
      clearRecoveryTimeout()
      setSessionStatus(SESSION_STATUS.INVALID)
    }

    let isMounted = true

    const initializeSession = async () => {
      try {
        const {
          data: { session }
        } = await supabase.auth.getSession()

        if (!isMounted) return

        if (session?.user) {
          markReady()
          return
        }

        if (!hasRecoveryHint) {
          markInvalid()
          return
        }

        recoveryWaitTimeoutRef.current = setTimeout(() => {
          if (!isMounted) return
          markInvalid()
        }, 3500)
      } catch (sessionError) {
        console.error('Unable to verify recovery session:', sessionError)
        if (isMounted) {
          markInvalid()
        }
      }
    }

    initializeSession()

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return

      if (
        (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') &&
        session?.user
      ) {
        markReady()
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
      clearRecoveryTimeout()
      clearRedirectTimeout()
    }
  }, [])

  const validatePassword = () => {
    if (!newPassword) {
      return 'Please enter a new password.'
    }

    if (newPassword.length < 6) {
      return 'Password must be at least 6 characters.'
    }

    if (newPassword !== confirmPassword) {
      return 'Passwords do not match.'
    }

    return ''
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    const validationError = validatePassword()
    if (validationError) {
      setError(validationError)
      return
    }

    setLoading(true)
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      })

      if (updateError) {
        throw updateError
      }

      setSuccessMessage('Your password was updated successfully. Redirecting to sign in...')
      setNewPassword('')
      setConfirmPassword('')

      redirectTimeoutRef.current = setTimeout(async () => {
        await supabase.auth.signOut().catch((signOutError) => {
          console.error('Error signing out after password reset:', signOutError)
        })

        navigate('/', { replace: true })
      }, 1500)
    } catch (saveError) {
      console.error('Failed to save new password:', saveError)
      setError('Unable to save your new password. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="recipe-app-shell min-h-screen bg-recipe-gradient flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-brand">Update Password</CardTitle>
          <CardDescription>
            Set your new password to finish account recovery.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {sessionStatus === SESSION_STATUS.CHECKING && (
            <div className="flex items-center justify-center rounded border border-gray-200 bg-gray-50 px-4 py-6">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-brand" />
            </div>
          )}

          {sessionStatus === SESSION_STATUS.INVALID && (
            <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              <p className="font-medium">Reset link is invalid or expired.</p>
              <p className="mt-1 text-sm">Please request a new password reset email and try again.</p>
            </div>
          )}

          {sessionStatus === SESSION_STATUS.READY && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-green-700 text-sm">
                  {successMessage}
                </div>
              )}

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <Input
                  id="newPassword"
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  placeholder="Enter your new password"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Confirm your new password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Saving...' : 'Save Password'}
              </Button>
            </form>
          )}
        </CardContent>

        <CardFooter className="justify-center">
          <Link to="/" className="text-sm text-brand hover:underline font-medium">
            Back to sign in
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}

export default UpdatePasswordPage

